<template>
  <div class="yl-root">
    <div class="yl-container">
      <!-- ===== Intro ===== -->
      <section class="yl-intro" id="intro">
        <div class="glow-orb orb-1"></div>
        <div class="glow-orb orb-2"></div>

        <span class="sparkle" style="top: 15%; left: 10%; --duration: 3s; --delay: 0s; font-size: 20px">✨</span>
        <span class="sparkle" style="top: 22%; right: 12%; --duration: 2.5s; --delay: 1s; font-size: 24px">✨</span>
        <span class="sparkle sparkle-light" style="top: 60%; left: 18%; --duration: 4s; --delay: 2s; font-size: 18px">🌟</span>
        <span class="sparkle" style="top: 50%; right: 20%; --duration: 3.5s; --delay: 0.5s; font-size: 20px">✨</span>
        <span class="sparkle" style="bottom: 25%; left: 25%; --duration: 4s; --delay: 1.5s; font-size: 16px">💖</span>

        <div class="intro-content">
          <div class="festival-badge">{{ locale.intro.badge }}</div>
          <img class="intro-logo" :src="require('@/assets/logo/lafia_logo_main.png')" alt="LAFIA" loading="eager" />
          <p class="intro-tagline">
            <span>{{ locale.intro.line1 }}</span
            ><br />
            <strong class="highlight">{{ locale.intro.line2 }}</strong
            ><br />
            <span class="since">{{ locale.intro.since }}</span>
          </p>
          <div class="utility-bar">
            <div class="lang-switcher" role="group" :aria-label="locale.intro.languageSelector">
              <button type="button" class="lang-btn" :class="{ active: currentLang === 'ko' }" @click="setLanguage('ko')">KR</button>
              <button type="button" class="lang-btn" :class="{ active: currentLang === 'en' }" @click="setLanguage('en')">EN</button>
              <button type="button" class="lang-btn" :class="{ active: currentLang === 'ja' }" @click="setLanguage('ja')">JP</button>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== Tab Nav ===== -->
      <nav class="yl-tab-nav" id="tabNav" role="tablist" :aria-label="locale.tabs.aria">
        <button v-for="tab in TABS" :key="tab.id" class="yl-tab-btn" :class="{ active: activeTab === tab.id }" role="tab" :aria-selected="activeTab === tab.id" @click="switchTab(tab.id)">
          <span v-if="tab.icon" class="yl-tab-icon">{{ tab.icon }}</span>
          {{ locale.tabs[tab.labelKey] }}
        </button>
      </nav>

      <!-- ===== Gallery Tab ===== -->
      <div v-if="activeTab === 'premium'" class="yl-gallery-section">
        <!-- Skeleton -->
        <template v-if="loading">
          <div class="yl-skeleton-grid">
            <div v-for="n in 6" :key="n" class="yl-skeleton-card">
              <div class="yl-skeleton-img"></div>
              <div class="yl-skeleton-body">
                <div class="yl-skeleton-line short"></div>
                <div class="yl-skeleton-line"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Error -->
        <div v-else-if="error" class="yl-gallery-error">
          <p>{{ locale.gallery.error }}</p>
          <button class="yl-retry-btn" @click="loadConcepts">{{ locale.gallery.retry }}</button>
        </div>

        <!-- Content -->
        <template v-else>
          <!-- Category Filter -->
          <div class="yl-category-filter">
            <button v-for="tagKey in tagList" :key="tagKey" class="yl-filter-chip" :class="{ active: activeTagKey === tagKey }" @click="activeTagKey = tagKey">
              {{ locale.gallery.tags[tagKey] || tagKey }}
            </button>
          </div>

          <!-- Count -->
          <div class="yl-gallery-count">{{ locale.gallery.count(filteredConcepts.length) }}</div>

          <!-- Grid -->
          <div v-if="filteredConcepts.length" class="yl-card-grid">
            <router-link v-for="item in filteredConcepts" :key="item.id" :to="getConceptLink(item.id)" class="yl-concept-card">
              <div class="yl-card-img-wrap">
                <img :src="'/upload/concept/thumbnail_image/' + item.pattern_1_image" :alt="item.title" loading="lazy" />
              </div>
              <div class="yl-card-body">
                <div v-if="item.tag" class="yl-card-tags">
                  <span v-for="t in item.tag.split(',')" :key="t" class="yl-tag-chip">
                    {{ locale.gallery.tags[TAG_KEY_BY_SOURCE[t.trim()]] || t.trim() }}
                  </span>
                </div>
                <div class="yl-card-title">{{ getConceptTitle(item) }}</div>
                <!-- <div v-if="getSubTitleForLocale(item)" class="yl-card-subtitle">{{ getSubTitleForLocale(item) }}</div> -->
              </div>
            </router-link>
          </div>

          <div v-else class="yl-gallery-error">
            <p>해당 조건의 컨셉이 없습니다</p>
          </div>
        </template>
      </div>

      <!-- ===== Cast Form Tab ===== -->
      <div v-if="activeTab === 'cast'" class="yl-cast-section">
        <!-- Success -->
        <div v-if="castSubmitted" class="yl-cast-success">
          <div class="yl-success-icon">🌸</div>
          <h3>{{ locale.cast.successTitle }}</h3>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p v-html="locale.cast.successBody"></p>
          <div class="yl-cast-success-note">{{ locale.cast.successNote }}</div>
          <div class="yl-cast-success-actions">
            <button class="yl-btn-secondary" @click="switchTab('premium')">{{ locale.cast.successBack }}</button>
            <button class="yl-btn-primary" @click="resetCastForm">{{ locale.cast.successAgain }}</button>
          </div>
        </div>

        <!-- Form -->
        <template v-else>
          <div class="yl-cast-title">{{ locale.cast.title }}</div>

          <form class="yl-cast-form" @submit.prevent="submitCast" novalidate>
            <!-- ===== Block 01: 기본 정보 ===== -->
            <section class="yl-cast-block">
              <div class="yl-cast-block-head">
                <span class="yl-cast-block-step">01</span>
                <div>
                  <div class="yl-cast-block-title">{{ locale.cast.basicTitle }}</div>
                </div>
              </div>

              <!-- 역할 선택 -->
              <div class="yl-form-group" :class="{ 'has-error': castErrors.role }">
                <div class="yl-role-cards">
                  <button
                    type="button"
                    class="yl-role-card"
                    :aria-pressed="castForm.role === 'mc' ? 'true' : 'false'"
                    @click="
                      castForm.role = 'mc'
                      castErrors.role = ''
                      resetCastStatus()
                    "
                  >
                    <span class="yl-role-icon">🎤</span>{{ locale.cast.roleMc }}
                    <span class="yl-role-desc">{{ locale.cast.roleMcDesc }}</span>
                  </button>
                  <button
                    type="button"
                    class="yl-role-card"
                    :aria-pressed="castForm.role === 'talent' ? 'true' : 'false'"
                    @click="
                      castForm.role = 'talent'
                      castErrors.role = ''
                      resetCastStatus()
                    "
                  >
                    <span class="yl-role-icon">⭐</span>{{ locale.cast.roleTalent }}
                    <span class="yl-role-desc">{{ locale.cast.roleTalentDesc }}</span>
                  </button>
                </div>
                <span v-if="castErrors.role" class="yl-form-error">{{ castErrors.role }}</span>
              </div>

              <div class="yl-cast-grid">
                <!-- 이름 -->
                <div class="yl-form-group" :class="{ 'has-error': castErrors.name }">
                  <div class="yl-field-shell">
                    <span class="yl-field-mini-label">{{ locale.cast.nameLabel }}</span>
                    <input
                      id="castName"
                      v-model="castForm.name"
                      type="text"
                      class="yl-form-input"
                      maxlength="20"
                      :placeholder="locale.cast.namePlaceholder"
                      :aria-label="locale.cast.nameLabel"
                      @input="
                        castErrors.name = ''
                        resetCastStatus()
                      "
                    />
                  </div>
                  <span v-if="castErrors.name" class="yl-form-error">{{ castErrors.name }}</span>
                </div>

                <!-- 전화번호 -->
                <div class="yl-form-group" :class="{ 'has-error': castErrors.phone }">
                  <div class="yl-field-shell">
                    <span class="yl-field-mini-label">{{ locale.cast.phoneLabel }}</span>
                    <input
                      id="castPhone"
                      v-model="castForm.phone"
                      type="text"
                      class="yl-form-input"
                      maxlength="40"
                      :placeholder="locale.cast.phonePlaceholder"
                      :aria-label="locale.cast.phoneLabel"
                      @input="
                        castErrors.phone = ''
                        resetCastStatus()
                      "
                    />
                  </div>
                  <span v-if="castErrors.phone" class="yl-form-error">{{ castErrors.phone }}</span>
                </div>

                <!-- 인스타 -->
                <div class="yl-form-group">
                  <div class="yl-field-shell">
                    <span class="yl-field-mini-label">{{ locale.cast.instagramLabel }}</span>
                    <input
                      id="castInstagram"
                      v-model="castForm.instagram"
                      type="text"
                      class="yl-form-input"
                      maxlength="100"
                      :placeholder="locale.cast.instagramPlaceholder"
                      :aria-label="locale.cast.instagramLabel"
                    />
                  </div>
                </div>

                <!-- 나이대 -->
                <div class="yl-form-group" :class="{ 'has-error': castErrors.age_range }">
                  <div class="yl-field-shell">
                    <span class="yl-field-mini-label">{{ locale.cast.ageLabel }}</span>
                    <select
                      id="castAge"
                      v-model="castForm.age_range"
                      class="yl-form-select"
                      :aria-label="locale.cast.ageLabel"
                      @change="
                        castErrors.age_range = ''
                        resetCastStatus()
                      "
                    >
                      <option value="">{{ locale.cast.agePlaceholder }}</option>
                      <option value="10대">{{ locale.cast.ageOptions.teens }}</option>
                      <option value="20대">{{ locale.cast.ageOptions.twenties }}</option>
                      <option value="30대">{{ locale.cast.ageOptions.thirties }}</option>
                      <option value="40대 이상">{{ locale.cast.ageOptions.forties }}</option>
                    </select>
                  </div>
                  <span v-if="castErrors.age_range" class="yl-form-error">{{ castErrors.age_range }}</span>
                </div>
              </div>

              <!-- 보호자 섹션 (10대) -->
              <div v-if="castForm.age_range === '10대'" class="yl-guardian-section" :class="{ 'has-error': castErrors.guardian }">
                <span class="yl-guardian-label">{{ locale.cast.guardianLabel }}</span>
                <input v-model="castForm.guardian_contact" type="text" class="yl-form-input" :placeholder="locale.cast.guardianPlaceholder" @input="castErrors.guardian = ''" />
                <div class="yl-checkbox-group">
                  <input id="guardianConsent" v-model="castForm.guardian_consent" type="checkbox" @change="castErrors.guardian = ''" />
                  <label for="guardianConsent">{{ locale.cast.guardianConsent }}</label>
                </div>
                <span v-if="castErrors.guardian" class="yl-form-error">{{ castErrors.guardian }}</span>
              </div>
            </section>

            <!-- ===== Block 02: 자기소개 ===== -->
            <section class="yl-cast-block">
              <div class="yl-cast-block-head">
                <span class="yl-cast-block-step">02</span>
                <div>
                  <div class="yl-cast-block-title">{{ locale.cast.profileTitle }}</div>
                </div>
              </div>

              <!-- 나의 특징 -->
              <div class="yl-form-group" :class="{ 'has-error': castErrors.my_trait }">
                <div class="yl-field-shell">
                  <span class="yl-field-mini-label">{{ locale.cast.traitLabel }}</span>
                  <textarea
                    id="castTrait"
                    v-model="castForm.my_trait"
                    class="yl-form-textarea"
                    :placeholder="locale.cast.traitPlaceholder"
                    :aria-label="locale.cast.traitLabel"
                    @input="
                      castErrors.my_trait = ''
                      resetCastStatus()
                    "
                  ></textarea>
                </div>
                <span v-if="castErrors.my_trait" class="yl-form-error">{{ castErrors.my_trait }}</span>
              </div>

              <!-- 사진 업로드 -->
              <div class="yl-form-group" :class="{ 'has-error': castErrors.photos }">
                <div class="yl-form-group-head">
                  <span class="yl-form-label">{{ locale.cast.photoLabel }} <span class="required">*</span></span>
                  <span class="yl-count-badge">{{ locale.cast.photoCount(castPhotos.length) }}</span>
                </div>
                <div class="yl-photo-upload-area">
                  <div v-for="(photo, i) in castPhotos" :key="'photo-' + i" class="yl-photo-slot filled">
                    <img :src="photo.url" :alt="locale.cast.photoAlt(i + 1)" />
                    <div v-if="photo.uploading" class="yl-photo-uploading">
                      <span class="yl-spinner" style="width: 14px; height: 14px; margin: 0"></span>
                    </div>
                    <button v-else type="button" class="yl-photo-remove" @click="removePhoto(i)">×</button>
                  </div>
                  <div v-if="castPhotos.length < 3" class="yl-photo-slot" @click="$refs.photoInput.click()">
                    <span class="yl-photo-add">+</span>
                  </div>
                </div>
                <span class="yl-form-hint yl-form-hint-emphasis">{{ locale.cast.photoHintPrimary }}</span>
                <span v-if="castErrors.photos" class="yl-form-error">{{ castErrors.photos }}</span>
                <input ref="photoInput" type="file" accept="image/jpeg,image/png,image/webp" multiple style="display: none" @change="handlePhotoInput" />
              </div>

              <!-- 영상 링크 -->
              <div class="yl-form-group">
                <label class="yl-form-label" for="castVideo">{{ locale.cast.videoLabel }}</label>
                <input id="castVideo" v-model="castForm.video_url" type="text" class="yl-form-input" :placeholder="locale.cast.videoPlaceholder" />
                <span class="yl-form-hint">{{ locale.cast.videoHint }}</span>
              </div>
            </section>

            <!-- ===== Block 03: 마지막 확인 ===== -->
            <section class="yl-cast-block">
              <div class="yl-cast-block-head">
                <span class="yl-cast-block-step">03</span>
                <div>
                  <div class="yl-cast-block-title">{{ locale.cast.consentTitle }}</div>
                </div>
              </div>

              <!-- cast-policy -->
              <section class="yl-cast-policy" :aria-label="locale.cast.policyAria">
                <div class="yl-cast-policy-head">
                  <span class="yl-cast-policy-badge">{{ locale.cast.policyBadge }}</span>
                  <p class="yl-cast-policy-lead">{{ locale.cast.policyLead }}</p>
                </div>
                <div class="yl-cast-policy-highlights">
                  <article class="yl-policy-highlight">
                    <strong>{{ locale.cast.highlightReviewTitle }}</strong>
                    <span>{{ locale.cast.highlightReviewDesc }}</span>
                  </article>
                  <article class="yl-policy-highlight">
                    <strong>{{ locale.cast.highlightContactTitle }}</strong>
                    <span>{{ locale.cast.highlightContactDesc }}</span>
                  </article>
                  <article class="yl-policy-highlight">
                    <strong>{{ locale.cast.highlightStoreTitle }}</strong>
                    <span>{{ locale.cast.highlightStoreDesc }}</span>
                  </article>
                </div>
                <p class="yl-cast-policy-foot">{{ locale.cast.policyFoot }}</p>
              </section>

              <!-- 개인정보 -->
              <div class="yl-form-group" :class="{ 'has-error': castErrors.privacy_consent }">
                <div class="yl-privacy-text">
                  <div class="yl-privacy-row">
                    <strong>{{ locale.cast.privacyCollectLabel }}</strong>
                    <span>{{ locale.cast.privacyCollectValue }}</span>
                  </div>
                  <div class="yl-privacy-row">
                    <strong>{{ locale.cast.privacyPurposeLabel }}</strong>
                    <span>{{ locale.cast.privacyPurposeValue }}</span>
                  </div>
                  <div class="yl-privacy-row">
                    <strong>{{ locale.cast.privacyRetentionLabel }}</strong>
                    <span>{{ locale.cast.privacyRetentionValue }}</span>
                  </div>
                  <div class="yl-privacy-row">
                    <strong>{{ locale.cast.privacyUsageLabel }}</strong>
                    <span>{{ locale.cast.privacyUsageValue }}</span>
                  </div>
                </div>
                <div class="yl-checkbox-group">
                  <input
                    id="privacyConsent"
                    v-model="castForm.privacy_consent"
                    type="checkbox"
                    @change="
                      castErrors.privacy_consent = ''
                      resetCastStatus()
                    "
                  />
                  <label for="privacyConsent">{{ locale.cast.privacyConsentLabel }} <span class="required">*</span></label>
                </div>
                <span v-if="castErrors.privacy_consent" class="yl-form-error">{{ castErrors.privacy_consent }}</span>
              </div>

              <!-- form-status -->
              <div v-if="castStatus.message" class="yl-form-status" :class="'yl-form-status--' + castStatus.type" role="status" aria-live="polite">
                {{ castStatus.message }}
              </div>

              <button type="submit" class="yl-cast-submit" :disabled="castSubmitting">
                <span v-if="castSubmitting" class="yl-spinner"></span>
                {{ castSubmitting ? locale.cast.submitting : locale.cast.submit }}
              </button>
            </section>
          </form>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const SUPPORTED_LANGS = ["ko", "en", "ja"]

const TAG_SOURCE_BY_KEY = {
  lovely: "러블리",
  dreamy: "몽환",
  cute: "큐티",
  fantasy: "판타지",
  kitsch: "키치",
  wedding: "웨딩",
}
const TAG_KEY_BY_SOURCE = Object.fromEntries(Object.entries(TAG_SOURCE_BY_KEY).map(([key, value]) => [value, key]))
const TAG_ORDER = ["lovely", "dreamy", "cute", "fantasy", "kitsch", "wedding"]

const LOCALES = {
  ko: {
    intro: {
      badge: "✨ LAFIA 릴스 페스티벌 ✨",
      line1: "나만의 동화 속 주인공이 되는 곳",
      line2: "대한민국 1위 공주스튜디오",
      since: "since 2018",
      languageSelector: "언어 선택",
    },
    tabs: { aria: "콘텐츠 카테고리", premium: "프리미엄 컨셉", cast: "출연 신청" },
    gallery: {
      error: "컨셉 정보를 불러오지 못했습니다",
      retry: "다시 시도",
      count: (n) => `${n}개의 컨셉`,
      tags: { all: "전체", lovely: "러블리", dreamy: "몽환", cute: "큐티", fantasy: "판타지", kitsch: "키치", wedding: "웨딩" },
    },
    cast: {
      title: "출연자 · MC 모집",
      policyAria: "신청 전 확인 사항",
      policyBadge: "신청 전 확인",
      policyLead: "모든 신청은 확인해요. 연락은 일정과 콘셉트가 맞는 분께만 드려요.",
      highlightReviewTitle: "7~14일",
      highlightReviewDesc: "보통 이 기간 안에 검토합니다.",
      highlightContactTitle: "연락 가는 경우",
      highlightContactDesc: "맞는 분께 전화, 문자, DM으로 연락해요.",
      highlightStoreTitle: "1년 보관",
      highlightStoreDesc: "비슷한 촬영이 생기면 다시 연락드릴 수 있어요.",
      policyFoot: "맞는 촬영이 없으면 연락이 없을 수 있어요. 사진과 링크는 검토할 때만 봅니다.",
      basicTitle: "기본 정보",
      roleLabel: "역할 선택",
      roleMc: "MC",
      roleMcDesc: "진행, 인터뷰, 리액션 중심",
      roleTalent: "출연자",
      roleTalentDesc: "체험, 모델, 참여형 촬영",
      nameLabel: "이름 (닉네임)",
      namePlaceholder: "이름 또는 닉네임",
      phoneLabel: "전화번호",
      phonePlaceholder: "010-0000-0000",
      instagramLabel: "인스타 아이디",
      instagramPlaceholder: "@인스타아이디 (없다면 생략)",
      ageLabel: "나이대",
      agePlaceholder: "선택해주세요",
      ageOptions: { teens: "10대", twenties: "20대", thirties: "30대", forties: "40대 이상" },
      guardianLabel: "👨‍👩‍👧 보호자 정보 (10대 필수)",
      guardianPlaceholder: "보호자 전화번호 (010-xxxx-xxxx)",
      guardianConsent: "보호자가 본 신청에 동의합니다",
      profileTitle: "자기소개",
      traitLabel: "나의 특징",
      traitPlaceholder: "자기소개를 편하게 적어주세요.\n예: 촬영 경험, 분위기, 특기\n10자 이상 작성",
      photoLabel: "사진 (1~3장)",
      photoCount: (n) => `${n} / 3장`,
      photoAlt: (n) => `사진 ${n}`,
      photoHintPrimary: "보정이 많이 들어간 사진은 선정되지 않습니다. 솔직하게 보정이 거의 없고 내추럴한 사진을 올리셔야 선정 확률이 높습니다. 출연자는 꼭 예쁜 사람만 찾지 않습니다.",
      videoLabel: "영상 링크 (선택)",
      videoPlaceholder: "인스타/유튜브/틱톡 URL",
      videoHint: "영상이 있으면 링크를 남겨주세요.",
      consentTitle: "마지막 확인",
      privacyCollectLabel: "받는 정보",
      privacyCollectValue: "이름, 전화번호, 인스타 아이디(선택), 나이대, 보호자 연락처(해당 시), 사진, 영상 링크",
      privacyPurposeLabel: "왜 받나요",
      privacyPurposeValue: "라피아 촬영 검토와 비슷한 촬영 연락",
      privacyRetentionLabel: "얼마나 보관하나요",
      privacyRetentionValue: "최대 1년 뒤 자동 삭제",
      privacyUsageLabel: "어디까지 쓰나요",
      privacyUsageValue: "사진과 링크는 검토할 때만 확인해요. 촬영이 확정되기 전에는 따로 안내 없이 외부에 올리지 않습니다.",
      privacyConsentLabel: "위 내용을 확인했고 동의합니다",
      submit: "신청하기",
      submitting: "신청 중...",
      statusFix: "표시된 곳만 고치면 바로 신청할 수 있어요.",
      statusCooldown: "이미 신청이 들어갔어요. 중복 접수를 막기 위해 5분 뒤 다시 시도해주세요.",
      statusNetwork: "네트워크를 확인한 뒤 다시 시도해주세요.",
      statusGeneric: "문제가 생겼어요. 다시 시도해주세요.",
      fileTooLarge: "사진 용량이 너무 커요. 최대 500KB까지 가능해요.",
      invalidFileType: "지원하지 않는 파일 형식이에요.",
      tooManyRequests: "요청이 많아요. 잠시 후 다시 시도해주세요.",
      successTitle: "신청이 접수됐어요",
      successBody: "모든 신청을 확인한 뒤, 일정과 콘셉트가 맞는 분께만 7~14일 안에 연락드려요.<br>맞는 촬영이 없으면 연락이 없을 수 있어요.",
      successNote: "연락은 남겨주신 전화번호나 인스타 DM으로 드려요. 정보는 최대 1년 보관했다가 자동 삭제됩니다.",
      successBack: "갤러리로 돌아가기",
      successAgain: "추가 신청",
      errors: {
        role: "역할을 선택해주세요.",
        name: "이름을 1~20자 안에서 적어주세요.",
        phone: "전화번호를 적어주세요.",
        age: "나이대를 선택해주세요.",
        guardian: "10대는 보호자 연락처와 동의가 필요해요.",
        my_trait: "자기소개를 10자 이상 적어주세요.",
        photos: "사진을 1장 이상 올려주세요.",
        privacy_consent: "위 내용을 확인하고 동의해주세요.",
      },
    },
  },
  en: {
    intro: {
      badge: "LAFIA Reels Project",
      line1: "Where your storybook self steps into the spotlight",
      line2: "Korea's Leading Princess Studio",
      since: "since 2018",
      languageSelector: "Language selector",
    },
    tabs: { aria: "Content categories", premium: "Premium Concepts", cast: "Casting Call" },
    gallery: {
      error: "We couldn't load the concepts.",
      retry: "Try again",
      count: (n) => `${n} concepts`,
      tags: { all: "All", lovely: "Lovely", dreamy: "Dreamy", cute: "Cute", fantasy: "Fantasy", kitsch: "Kitsch", wedding: "Wedding" },
    },
    cast: {
      title: "Casting Call · MC & Talent",
      policyAria: "Before you apply",
      policyBadge: "Before You Apply",
      policyLead: "We read every application. We reach out only when the concept and schedule line up.",
      highlightReviewTitle: "7-14 days",
      highlightReviewDesc: "Most applications are reviewed within this window.",
      highlightContactTitle: "Who We Contact",
      highlightContactDesc: "If it is a fit, we may call, text, or DM you.",
      highlightStoreTitle: "Stored 1 year",
      highlightStoreDesc: "We may reach out later if a similar shoot comes up.",
      policyFoot: "If nothing fits right now, you may not hear from us. Photos and links are reviewed only for casting.",
      basicTitle: "Basic Details",
      roleLabel: "Role",
      roleMc: "MC",
      roleMcDesc: "Hosting, interviews, and on-camera reactions",
      roleTalent: "Talent",
      roleTalentDesc: "Modeling, participation, and experience-based content",
      nameLabel: "Name / Nickname",
      namePlaceholder: "Name or nickname",
      phoneLabel: "Phone",
      phonePlaceholder: "Phone number",
      instagramLabel: "Instagram",
      instagramPlaceholder: "@instagram_handle (skip if none)",
      ageLabel: "Age Range",
      agePlaceholder: "Choose one",
      ageOptions: { teens: "Teens", twenties: "20s", thirties: "30s", forties: "40+" },
      guardianLabel: "Guardian Details (Required for Teens)",
      guardianPlaceholder: "Guardian phone number",
      guardianConsent: "My guardian agrees to this application",
      profileTitle: "About You",
      traitLabel: "What makes you stand out?",
      traitPlaceholder: "Tell us about yourself.\nExample: on-camera experience, vibe, or strengths\n10+ characters",
      photoLabel: "Photos (1-3)",
      photoCount: (n) => `${n} / 3 photos`,
      photoAlt: (n) => `Photo ${n}`,
      photoHintPrimary: "Heavily edited photos are unlikely to be selected. Natural photos with little retouching give you a better chance. We are not only looking for conventionally pretty faces.",
      videoLabel: "Video Link (Optional)",
      videoPlaceholder: "Instagram / YouTube / TikTok URL",
      videoHint: "Add a link if you have a video that shows you well.",
      consentTitle: "Final Check",
      privacyCollectLabel: "What we collect",
      privacyCollectValue: "Name, phone number, Instagram handle (optional), age range, guardian contact (if applicable), photos, and video link",
      privacyPurposeLabel: "Why we ask",
      privacyPurposeValue: "Casting review for LAFIA shoots and follow-up for similar projects",
      privacyRetentionLabel: "How long we keep it",
      privacyRetentionValue: "Up to 1 year, then deleted automatically",
      privacyUsageLabel: "How we use it",
      privacyUsageValue: "Photos and video links are reviewed only for casting. Nothing is posted publicly before a shoot is confirmed.",
      privacyConsentLabel: "I reviewed this and agree",
      submit: "Submit Application",
      submitting: "Submitting...",
      statusFix: "Fix the highlighted fields and try again.",
      statusCooldown: "You have already applied. Please wait 5 minutes before submitting again.",
      statusNetwork: "Please check your network connection and try again.",
      statusGeneric: "Something went wrong. Please try again.",
      fileTooLarge: "Your photo file is too large. (Max 500KB)",
      invalidFileType: "This file type is not supported.",
      tooManyRequests: "Too many requests. Please try again in a moment.",
      successTitle: "Your application is in",
      successBody:
        "We review every application and contact only the people who match the concept and schedule within 7-14 days.<br>If there is no matching shoot, you may not receive a separate reply.",
      successNote: "We will contact you through the phone number or Instagram account you shared. Your information is stored for up to 1 year and then deleted.",
      successBack: "Back to Gallery",
      successAgain: "Apply Again",
      errors: {
        role: "Please choose a role.",
        name: "Please enter a name between 1 and 20 characters.",
        phone: "Please enter your phone number.",
        age: "Please select your age range.",
        guardian: "Teen applicants must provide a guardian contact and consent.",
        my_trait: "Please tell us about yourself in at least 10 characters.",
        photos: "Please upload at least 1 photo.",
        privacy_consent: "Please agree to the information usage policy.",
      },
    },
  },
  ja: {
    intro: {
      badge: "LAFIA リールプロジェクト",
      line1: "物語の主人公になれる場所",
      line2: "韓国を代表するプリンセススタジオ",
      since: "since 2018",
      languageSelector: "言語選択",
    },
    tabs: { aria: "コンテンツカテゴリー", premium: "プレミアムコンセプト", cast: "出演応募" },
    gallery: {
      error: "コンセプト情報を読み込めませんでした。",
      retry: "再試行",
      count: (n) => `${n}件のコンセプト`,
      tags: {
        all: "すべて",
        lovely: "ラブリー",
        dreamy: "幻想的",
        cute: "キュート",
        fantasy: "ファンタジー",
        kitsch: "キッチュ",
        wedding: "ウェディング",
      },
    },
    cast: {
      title: "出演者・MC 募集",
      policyAria: "応募前の確認事項",
      policyBadge: "応募前に確認",
      policyLead: "すべての応募を確認します。企画内容と日程が合う方にのみご連絡します。",
      highlightReviewTitle: "7〜14日",
      highlightReviewDesc: "通常この期間内に確認します。",
      highlightContactTitle: "連絡する場合",
      highlightContactDesc: "条件が合う方に電話・メッセージ・DMでご連絡します。",
      highlightStoreTitle: "1年間保管",
      highlightStoreDesc: "似た撮影があれば、あとからご連絡する場合があります。",
      policyFoot: "合う撮影がない場合はご連絡がないことがあります。写真とリンクは選考確認のためだけに見ます。",
      basicTitle: "基本情報",
      roleLabel: "役割",
      roleMc: "MC",
      roleMcDesc: "進行・インタビュー・リアクション中心",
      roleTalent: "出演者",
      roleTalentDesc: "撮影モデル・体験・参加型コンテンツ",
      nameLabel: "名前（ニックネーム）",
      namePlaceholder: "名前またはニックネーム",
      phoneLabel: "電話番号",
      phonePlaceholder: "電話番号",
      instagramLabel: "Instagram ID",
      instagramPlaceholder: "@instagram_id（なければ省略）",
      ageLabel: "年齢層",
      agePlaceholder: "選択してください",
      ageOptions: { teens: "10代", twenties: "20代", thirties: "30代", forties: "40代以上" },
      guardianLabel: "保護者情報（10代は必須）",
      guardianPlaceholder: "保護者の電話番号",
      guardianConsent: "保護者は本応募に同意しています",
      profileTitle: "自己紹介",
      traitLabel: "あなたの魅力",
      traitPlaceholder: "自己紹介を気軽に書いてください。\n例: 撮影経験、雰囲気、特技\n10文字以上",
      photoLabel: "写真（1〜3枚）",
      photoCount: (n) => `${n} / 3枚`,
      photoAlt: (n) => `写真 ${n}`,
      photoHintPrimary: "加工の強い写真は選ばれにくいです。補正が少ない自然な写真のほうが選ばれやすくなります。出演者は「きれいな人だけ」を探しているわけではありません。",
      videoLabel: "動画リンク（任意）",
      videoPlaceholder: "Instagram / YouTube / TikTok URL",
      videoHint: "動画があればリンクを残してください。",
      consentTitle: "最後の確認",
      privacyCollectLabel: "受け取る情報",
      privacyCollectValue: "名前、電話番号、Instagram ID（任意）、年齢層、保護者連絡先（該当時）、写真、動画リンク",
      privacyPurposeLabel: "なぜ必要か",
      privacyPurposeValue: "LAFIA の撮影検討と似た撮影のご連絡",
      privacyRetentionLabel: "保管期間",
      privacyRetentionValue: "最大1年後に自動削除",
      privacyUsageLabel: "どこまで使うか",
      privacyUsageValue: "写真と動画リンクは選考確認のためだけに見ます。撮影確定前に外部公開へ使うことはありません。",
      privacyConsentLabel: "上記を確認し、同意します",
      submit: "応募する",
      submitting: "送信中...",
      statusFix: "表示された項目を修正すると、そのまま送信できます。",
      statusCooldown: "すでに応募済みです。重複応募防止のため、5分後にもう一度お試しください。",
      statusNetwork: "通信状況を確認してから、もう一度お試しください。",
      statusGeneric: "エラーが発生しました。もう一度お試しください。",
      fileTooLarge: "写真ファイルのサイズが大きすぎます。（最大 500KB）",
      invalidFileType: "対応していないファイル形式です。",
      tooManyRequests: "リクエストが多すぎます。少し時間をおいて再度お試しください。",
      successTitle: "応募を受け付けました",
      successBody: "すべての応募を確認し、企画内容と日程が合う方にのみ 7〜14日以内にご連絡します。<br>合う撮影がない場合は個別連絡がないことがあります。",
      successNote: "ご連絡は電話番号または Instagram DM に行います。情報は最大1年間保管した後、自動削除されます。",
      successBack: "ギャラリーに戻る",
      successAgain: "もう一度応募する",
      errors: {
        role: "役割を選択してください。",
        name: "名前を 1〜20文字で入力してください。",
        phone: "電話番号を入力してください。",
        age: "年齢層を選択してください。",
        guardian: "10代の応募には保護者連絡先と同意が必要です。",
        my_trait: "あなたの魅力を 10文字以上で入力してください。",
        photos: "写真を最低1枚アップロードしてください。",
        privacy_consent: "情報利用ポリシーへの同意が必要です。",
      },
    },
  },
}

const TABS = [
  { id: "premium", labelKey: "premium", icon: "👑", productType: "premium" },
  { id: "cast", labelKey: "cast", icon: "🎬" },
]

function defaultCastForm() {
  return {
    role: "",
    name: "",
    phone: "",
    instagram: "",
    age_range: "",
    guardian_contact: "",
    guardian_consent: false,
    my_trait: "",
    video_url: "",
    privacy_consent: false,
  }
}

function defaultCastErrors() {
  return {
    role: "",
    name: "",
    phone: "",
    age_range: "",
    guardian: "",
    my_trait: "",
    photos: "",
    privacy_consent: "",
  }
}

import { mapState, mapMutations } from "vuex"

export default {
  name: "WebsiteYoutubeLink",

  data: () => ({
    TABS,
    TAG_KEY_BY_SOURCE,
    activeTab: TABS[0].id,

    // Gallery
    concepts: [],
    loading: false,
    error: false,
    activeTagKey: "all",
    galleryRequestToken: 0,

    // Cast form
    castForm: defaultCastForm(),
    castPhotos: [],
    castErrors: defaultCastErrors(),
    castStatus: { message: "", type: "" },
    castSubmitting: false,
    castSubmitted: false,
  }),

  computed: {
    ...mapState(["lang"]),

    currentLang() {
      if (this.lang === "jp") return "ja"
      if (SUPPORTED_LANGS.includes(this.lang)) return this.lang
      return "ko"
    },

    locale() {
      return LOCALES[this.currentLang] || LOCALES.ko
    },

    tagList() {
      const usedTags = new Set()
      this.concepts.forEach((c) => {
        if (c.tag) {
          c.tag.split(",").forEach((t) => {
            const key = TAG_KEY_BY_SOURCE[t.trim()]
            if (key) usedTags.add(key)
          })
        }
      })
      return ["all", ...TAG_ORDER.filter((key) => usedTags.has(key))]
    },

    filteredConcepts() {
      if (this.activeTagKey === "all") return this.concepts
      const sourceTag = TAG_SOURCE_BY_KEY[this.activeTagKey]
      if (!sourceTag) return this.concepts
      return this.concepts.filter(
        (c) =>
          c.tag &&
          c.tag
            .split(",")
            .map((t) => t.trim())
            .includes(sourceTag)
      )
    },
  },

  mounted() {
    const urlLang = this.$route?.query?.lang
    if (SUPPORTED_LANGS.includes(urlLang) && urlLang !== this.currentLang) {
      this.SET_LANG(urlLang === "ja" ? "jp" : urlLang)
    }
    this.loadConcepts()
  },

  methods: {
    ...mapMutations(["SET_LANG"]),

    // ===== Language =====
    setLanguage(lang) {
      if (!SUPPORTED_LANGS.includes(lang)) return
      this.SET_LANG(lang === "ja" ? "jp" : lang)
      const query = { ...this.$route.query }
      if (lang === "ko") delete query.lang
      else query.lang = lang
      this.$router.replace({ query }).catch(() => {})
    },

    // ===== Tabs =====
    switchTab(tabId) {
      this.activeTab = tabId
      this.galleryRequestToken++
      if (tabId !== "cast") {
        this.loadConcepts()
      } else {
        this.castErrors = defaultCastErrors()
        this.castStatus = { message: "", type: "" }
      }
    },

    // ===== Gallery =====
    loadConcepts() {
      if (this.activeTab !== "premium") return
      const token = ++this.galleryRequestToken
      this.loading = true
      this.error = false
      this.$http
        .post("/api/concept/select", {
          params: { productType: "premium" },
        })
        .then((res) => {
          if (token !== this.galleryRequestToken) return
          this.concepts = res.data || []
        })
        .catch(() => {
          if (token !== this.galleryRequestToken) return
          this.error = true
        })
        .finally(() => {
          if (token !== this.galleryRequestToken) return
          this.loading = false
        })
    },

    getConceptLink(conceptId) {
      const query = {}
      if (this.$route.query.event) {
        query.event = this.$route.query.event
      }
      return { path: `/castle/${conceptId}`, query }
    },

    getConceptTitle(concept) {
      switch (this.currentLang) {
        case "en":
          return concept.title_en || concept.title
        case "ja":
          return concept.title_jp || concept.title
        default:
          return concept.title
      }
    },

    getSubTitleForLocale(item) {
      return item?.concept_desc?.subTitle?.[this.currentLang] || item?.concept_desc?.subTitle?.en || item?.concept_desc?.subTitle?.ja || item?.concept_desc?.subTitle?.kr || ""
    },

    // ===== Photos =====
    async handlePhotoInput(e) {
      const files = Array.from(e.target.files)
      for (const file of files) {
        if (this.castPhotos.length >= 3) break
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
          this.showCastStatus(this.locale.cast.invalidFileType, "error")
          continue
        }
        const preview = URL.createObjectURL(file)
        const idx = this.castPhotos.length
        this.castPhotos.push({ url: preview, filename: null, uploading: true })
        try {
          const blob = await this.compressImage(file)
          const formData = new FormData()
          formData.append("image", blob, file.name)
          const res = await this.$http.post("/api/image/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            params: { type: "website_youtube_link" },
          })
          this.castPhotos[idx].filename = res.data
          this.castPhotos[idx].uploading = false
          URL.revokeObjectURL(preview)
          this.castPhotos[idx].url = "/upload/website_youtube_link/" + res.data
          if (this.castErrors.photos) this.castErrors.photos = ""
        } catch (err) {
          console.error("Photo upload error:", err)
          this.castPhotos.splice(idx, 1)
          this.showCastStatus("사진 업로드에 실패했습니다. 다시 시도해주세요.", "error")
        }
      }
      e.target.value = ""
    },

    removePhoto(index) {
      this.castPhotos.splice(index, 1)
    },

    compressImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        const objectUrl = URL.createObjectURL(file)
        img.onload = () => {
          URL.revokeObjectURL(objectUrl)
          const MAX = 1200
          let w = img.width,
            h = img.height
          if (w > MAX || h > MAX) {
            if (w > h) {
              h = Math.round((h * MAX) / w)
              w = MAX
            } else {
              w = Math.round((w * MAX) / h)
              h = MAX
            }
          }
          const canvas = document.createElement("canvas")
          canvas.width = w
          canvas.height = h
          canvas.getContext("2d").drawImage(img, 0, 0, w, h)
          canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/jpeg", 0.7)
        }
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          reject(new Error("Image load failed"))
        }
        img.src = objectUrl
      })
    },

    // ===== Form Status =====
    showCastStatus(message, type = "info") {
      this.castStatus = { message, type }
    },

    resetCastStatus() {
      this.castStatus = { message: "", type: "" }
    },

    focusCastTarget(targetId) {
      this.$nextTick(() => {
        const el = this.$el.querySelector(`#${targetId}`)
        if (!el) return
        el.scrollIntoView({ behavior: "smooth", block: "center" })
        if (typeof el.focus === "function") el.focus({ preventScroll: true })
      })
    },

    // ===== Cast Submit =====
    async submitCast() {
      const L = this.locale

      // Reset feedback
      this.castErrors = defaultCastErrors()
      this.resetCastStatus()

      // Cooldown check
      try {
        const lastSubmit = localStorage.getItem("cast_cooldown")
        if (lastSubmit && Date.now() - parseInt(lastSubmit) < 5 * 60 * 1000) {
          this.showCastStatus(L.cast.statusCooldown, "info")
          return
        }
      } catch (_) {}

      // Validate
      const errors = []
      if (!this.castForm.role) errors.push({ key: "role", targetId: "roleCardMc" })
      if (!this.castForm.name || this.castForm.name.length > 20) errors.push({ key: "name", targetId: "castName" })
      if (!this.castForm.phone) errors.push({ key: "phone", targetId: "castPhone" })
      if (!this.castForm.age_range) errors.push({ key: "age_range", targetId: "castAge" })
      if (this.castForm.age_range === "10대") {
        if (!this.castForm.guardian_contact || !this.castForm.guardian_consent) {
          errors.push({ key: "guardian", targetId: "guardianContact" })
        }
      }
      if (!this.castForm.my_trait || this.castForm.my_trait.length < 10) errors.push({ key: "my_trait", targetId: "castTrait" })
      if (this.castPhotos.length === 0) errors.push({ key: "photos", targetId: "photoAddSlot" })
      if (this.castPhotos.some((p) => p.uploading)) {
        this.showCastStatus("사진 업로드가 완료될 때까지 기다려주세요.", "info")
        return
      }
      if (!this.castForm.privacy_consent) errors.push({ key: "privacy_consent", targetId: "privacyConsent" })

      if (errors.length > 0) {
        errors.forEach((err) => {
          const msg = L.cast.errors[err.key]
          if (msg) this.castErrors[err.key] = msg
        })
        this.showCastStatus(L.cast.statusFix, "error")
        this.focusCastTarget(errors[0].targetId)
        return
      }

      this.castSubmitting = true
      try {
        const res = await this.$http.post("/api/website_youtube_link/insert", {
          params: {
            role: this.castForm.role,
            name: this.castForm.name,
            phone: this.castForm.phone,
            contact: this.castForm.phone,
            instagram: this.castForm.instagram,
            age_range: this.castForm.age_range,
            guardian_contact: this.castForm.age_range === "10대" ? this.castForm.guardian_contact : null,
            my_trait: this.castForm.my_trait,
            photos: this.castPhotos.map((p) => p.filename),
            video_url: this.castForm.video_url || null,
          },
        })
        if (res.data.success) {
          try {
            localStorage.setItem("cast_cooldown", Date.now().toString())
          } catch (_) {}
          this.castSubmitted = true
        } else {
          const error = res.data?.error || {}
          const msgs = {
            CAST_COOLDOWN: L.cast.statusCooldown,
            FILE_TOO_LARGE: L.cast.fileTooLarge,
            INVALID_FILE_TYPE: L.cast.invalidFileType,
            TOO_MANY_REQUESTS: L.cast.tooManyRequests,
          }
          this.showCastStatus(msgs[error.code] || error.message || L.cast.statusGeneric, "error")
        }
      } catch (err) {
        console.error("Submit error:", err)
        this.showCastStatus(L.cast.statusNetwork, "error")
      } finally {
        this.castSubmitting = false
      }
    },

    resetCastForm() {
      this.castSubmitted = false
      this.castForm = defaultCastForm()
      this.castPhotos = []
      this.castErrors = defaultCastErrors()
      this.castStatus = { message: "", type: "" }
    },
  },
}
</script>

<style scoped>
/* ===== Reset ===== */
.yl-root {
  font-family: "Noto Sans KR", "Noto Sans JP", "Apple SD Gothic Neo", sans-serif;
  background: #fdf8f5;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #3d2c2e;
}

.yl-container {
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  background: #faf6f3;
  box-shadow: 0 0 40px rgba(134, 102, 92, 0.08);
  position: relative;
  overflow: hidden;
}

/* ===== CSS Variables ===== */
.yl-container,
.yl-root {
  --color-brand: #86665c;
  --color-brand-light: #a68b82;
  --color-rose: #c4958a;
  --color-cream: #fdf8f5;
  --color-warm-white: #faf6f3;
  --color-text: #3d2c2e;
  --color-text-light: #8b7d76;
  --color-border: #e8ddd8;
  --font-brand: "Cormorant Garamond", Georgia, serif;
  --radius-pill: 24px;
  --transition: 0.3s ease;
}

/* ===== Intro ===== */
.yl-intro {
  padding: 12px 24px 2px;
  text-align: center;
  position: relative;
  background: linear-gradient(135deg, #fffcfb 0%, #fef5ef 50%, #fdf0f2 100%);
  overflow: hidden;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
  z-index: 0;
}

.orb-1 {
  top: -10%;
  left: -10%;
  width: 300px;
  height: 300px;
  background: rgba(196, 149, 138, 0.25);
  animation: floatOrb 10s ease-in-out infinite alternate;
}

.orb-2 {
  bottom: -10%;
  right: -10%;
  width: 250px;
  height: 250px;
  background: rgba(212, 165, 116, 0.25);
  animation: floatOrb 12s ease-in-out infinite alternate-reverse;
}

@keyframes floatOrb {
  0% {
    transform: translateY(0) scale(1);
  }
  100% {
    transform: translateY(-30px) scale(1.1);
  }
}

.intro-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.festival-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 22px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  color: #8a6c62;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  border-radius: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  opacity: 0;
  animation: dropIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.intro-logo {
  width: 150px;
  height: auto;
  display: block;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 15px rgba(196, 149, 138, 0.4));
  opacity: 0;
  animation: dropIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s forwards;
}

@keyframes dropIn {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.intro-tagline {
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
  letter-spacing: 0.04em;
  line-height: 1.6;
  margin-bottom: 14px;
  opacity: 0;
  animation: dropIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards;
  word-break: keep-all;
  overflow-wrap: break-word;
}

.intro-tagline .highlight {
  font-size: 19px;
  color: var(--color-brand);
  font-weight: 700;
  line-height: 1.8;
  letter-spacing: 0.02em;
}

.intro-tagline .since {
  display: block;
  margin-top: 10px;
  font-style: italic;
  font-family: var(--font-brand);
  font-size: 12px;
  letter-spacing: 0.15em;
  color: var(--color-brand-light);
}

/* ===== Lang Switcher ===== */
.utility-bar {
  display: flex;
  justify-content: center;
  margin-top: 0;
  z-index: 2;
}

.lang-switcher {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 18px rgba(134, 102, 92, 0.12);
}

.lang-btn {
  border: none;
  background: transparent;
  color: var(--color-text-light);
  font-family: inherit;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 6px 8px;
  border-radius: 999px;
  cursor: pointer;
  transition: var(--transition);
  min-width: 32px;
}

.lang-btn:hover {
  color: var(--color-text);
  background: rgba(196, 149, 138, 0.08);
}

.lang-btn.active {
  background: linear-gradient(135deg, rgba(134, 102, 92, 0.94), rgba(166, 139, 130, 0.94));
  color: #fff;
  box-shadow: 0 8px 18px rgba(134, 102, 92, 0.18);
}

.sparkle {
  position: absolute;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  animation: twinkle var(--duration, 3s) ease-in-out infinite;
  animation-delay: var(--delay, 0s);
}

.sparkle-light {
  opacity: 0.6;
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0;
    transform: scale(0.5) rotate(0deg);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.2) rotate(20deg);
  }
}

/* ===== Tab Nav ===== */
.yl-tab-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--color-warm-white);
  padding: 16px 20px 0;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: 1px solid var(--color-border);
}

.yl-tab-nav::-webkit-scrollbar {
  display: none;
}

.yl-tab-btn {
  flex-shrink: 0;
  padding: 10px 20px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 400;
  color: var(--color-text-light);
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
  letter-spacing: 0.02em;
  margin-bottom: -1px;
}

.yl-tab-btn:hover {
  color: var(--color-brand);
}

.yl-tab-btn.active {
  color: var(--color-brand);
  font-weight: 500;
  border-bottom-color: var(--color-brand);
}

.yl-tab-icon {
  margin-right: 4px;
  font-size: 12px;
}

/* ===== Gallery ===== */
.yl-gallery-section {
  padding: 0 12px 24px;
}

.yl-category-filter {
  display: flex;
  gap: 6px;
  padding: 14px 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.yl-category-filter::-webkit-scrollbar {
  display: none;
}

.yl-filter-chip {
  flex-shrink: 0;
  padding: 6px 16px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border);
  background: transparent;
  font-size: 12px;
  color: var(--color-text-light);
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
}

.yl-filter-chip:hover {
  border-color: var(--color-rose);
  color: var(--color-brand);
}

.yl-filter-chip.active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #fff;
}

.yl-card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.yl-concept-card {
  display: block;
  text-decoration: none;
  color: var(--color-text);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 12px rgba(134, 102, 92, 0.08);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.yl-concept-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(134, 102, 92, 0.15);
}

.yl-card-img-wrap {
  aspect-ratio: 3/4;
  overflow: hidden;
  position: relative;
  background: var(--color-cream);
  border-radius: 12px;
}

.yl-card-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.3s ease;
}

.yl-concept-card:hover .yl-card-img-wrap img {
  transform: scale(1.04);
}

.yl-card-body {
  padding: 10px 10px 12px;
}

.yl-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.yl-tag-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 400;
  background: rgba(196, 149, 138, 0.12);
  color: var(--color-brand);
  letter-spacing: 0.02em;
}

.yl-card-title {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--color-text);
}

.yl-card-subtitle {
  font-size: 11px;
  font-weight: 300;
  color: var(--color-text-light);
  margin-top: 3px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: keep-all;
}

.yl-gallery-count {
  text-align: right;
  font-size: 11px;
  color: var(--color-text-light);
  padding: 0 2px 8px;
  letter-spacing: 0.02em;
}

/* ===== Skeleton ===== */
.yl-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 14px 0;
}

.yl-skeleton-card {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 12px rgba(134, 102, 92, 0.06);
}

.yl-skeleton-img {
  aspect-ratio: 3/4;
  background: linear-gradient(110deg, #f0ebe8 30%, #f7f3f0 50%, #f0ebe8 70%);
  background-size: 200% 100%;
  animation: shimmer 1.5s linear infinite;
}

.yl-skeleton-body {
  padding: 10px;
}

.yl-skeleton-line {
  height: 10px;
  border-radius: 5px;
  background: linear-gradient(110deg, #f0ebe8 30%, #f7f3f0 50%, #f0ebe8 70%);
  background-size: 200% 100%;
  animation: shimmer 1.5s linear infinite;
  margin-bottom: 6px;
}

.yl-skeleton-line.short {
  width: 60%;
}

/* ===== Error ===== */
.yl-gallery-error {
  text-align: center;
  padding: 60px 20px;
}

.yl-gallery-error p {
  font-size: 13px;
  color: var(--color-text-light);
  margin-bottom: 16px;
  word-break: keep-all;
}

.yl-retry-btn {
  display: inline-block;
  padding: 10px 24px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: transparent;
  font-size: 12px;
  color: var(--color-brand);
  cursor: pointer;
  transition: var(--transition);
}

.yl-retry-btn:hover {
  background: var(--color-cream);
  border-color: var(--color-rose);
}

/* ===== Cast Section ===== */
.yl-cast-section {
  padding: 24px 16px 48px;
}

.yl-cast-title {
  font-family: var(--font-brand);
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  color: var(--color-text);
  margin-bottom: 16px;
}

.yl-cast-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ===== Cast Block ===== */
.yl-cast-block {
  padding: 24px 20px;
  border-radius: 16px;
  border: 1px solid rgba(196, 149, 138, 0.14);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 8px 20px rgba(134, 102, 92, 0.04);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.yl-cast-block-head {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.yl-cast-block-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: rgba(196, 149, 138, 0.12);
  color: var(--color-brand);
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.yl-cast-block-title {
  font-size: 16px;
  font-weight: 650;
  color: var(--color-text);
  line-height: 1.35;
  padding-top: 8px;
}

.yl-cast-grid {
  display: grid;
  gap: 16px;
}

/* ===== Form Group ===== */
.yl-form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.yl-form-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.yl-form-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text);
  word-break: keep-all;
}

.required {
  color: var(--color-rose);
  margin-left: 2px;
}

/* ===== Field Shell (Floating Label) ===== */
.yl-field-shell {
  position: relative;
}

.yl-field-mini-label {
  position: absolute;
  top: 11px;
  left: 16px;
  z-index: 1;
  pointer-events: none;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: rgba(89, 70, 63, 0.7);
  transition: color 0.2s ease;
}

.yl-field-shell:focus-within .yl-field-mini-label {
  color: var(--color-brand);
}

/* ===== Inputs ===== */
.yl-form-input,
.yl-form-select,
.yl-form-textarea {
  width: 100%;
  min-height: 48px;
  padding: 0 16px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  font-size: 14px;
  color: var(--color-text);
  background: #fff;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  outline: none;
  font-family: inherit;
}

.yl-field-shell .yl-form-input,
.yl-field-shell .yl-form-select {
  min-height: 56px;
  padding-top: 18px;
  padding-bottom: 8px;
}

.yl-form-input::placeholder,
.yl-form-textarea::placeholder {
  color: rgba(89, 70, 63, 0.58);
}

.yl-form-input:focus,
.yl-form-select:focus,
.yl-form-textarea:focus {
  border-color: var(--color-rose);
  box-shadow: 0 0 0 4px rgba(196, 149, 138, 0.12);
}

.yl-form-textarea {
  min-height: 144px;
  padding: 16px;
  resize: vertical;
  line-height: 1.5;
}

.yl-field-shell .yl-form-textarea {
  min-height: 156px;
  padding-top: 30px;
}

/* ===== Has Error ===== */
.yl-form-group.has-error .yl-form-input,
.yl-form-group.has-error .yl-form-select,
.yl-form-group.has-error .yl-form-textarea {
  border-color: #e17474;
  box-shadow: 0 0 0 3px rgba(225, 116, 116, 0.12);
}

.yl-form-group.has-error .yl-role-cards,
.yl-form-group.has-error .yl-photo-upload-area {
  padding: 10px;
  border-radius: 12px;
  background: rgba(225, 116, 116, 0.05);
  border: 1px solid rgba(225, 116, 116, 0.16);
}

.yl-form-group.has-error .yl-field-mini-label {
  color: #cf4b3b;
}

.yl-guardian-section.has-error {
  border-color: #e17474;
  box-shadow: 0 0 0 3px rgba(225, 116, 116, 0.12);
}

/* ===== Form Error / Hint ===== */
.yl-form-error {
  font-size: 11px;
  color: #e74c3c;
  line-height: 1.45;
  word-break: keep-all;
}

.yl-form-hint {
  max-width: 44ch;
  font-size: 12px;
  color: var(--color-text-light);
  line-height: 1.6;
  word-break: keep-all;
  overflow-wrap: break-word;
}

.yl-form-hint-emphasis {
  max-width: 48ch;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(196, 149, 138, 0.08);
  background: rgba(196, 149, 138, 0.04);
  color: var(--color-text-light);
}

/* ===== Form Status ===== */
.yl-form-status {
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.6;
  word-break: keep-all;
}

.yl-form-status--info {
  background: rgba(134, 102, 92, 0.08);
  color: var(--color-brand);
}

.yl-form-status--error {
  background: rgba(225, 116, 116, 0.08);
  color: #b45454;
}

.yl-form-status--success {
  background: rgba(196, 149, 138, 0.12);
  color: var(--color-brand);
}

/* ===== Role Cards ===== */
.yl-role-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.yl-role-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 128px;
  padding: 16px 14px;
  border: 2px solid var(--color-border);
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  text-align: center;
  transition: var(--transition);
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
  font-family: inherit;
}

.yl-role-card:hover {
  border-color: var(--color-rose);
}

.yl-role-card[aria-pressed="true"] {
  border-color: var(--color-rose);
  background: linear-gradient(135deg, rgba(196, 149, 138, 0.08), rgba(212, 165, 116, 0.08));
  box-shadow: 0 0 0 1px var(--color-rose);
}

.yl-role-icon {
  font-size: 24px;
  display: block;
  margin-bottom: 6px;
}

.yl-role-desc {
  font-size: 12px;
  font-weight: 400;
  color: var(--color-text-light);
  margin-top: 4px;
  line-height: 1.45;
  word-break: keep-all;
}

/* ===== Count Badge ===== */
.yl-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(134, 102, 92, 0.08);
  color: var(--color-brand);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

/* ===== Guardian ===== */
.yl-guardian-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: rgba(196, 149, 138, 0.06);
  border-radius: 12px;
  border: 1px solid rgba(196, 149, 138, 0.15);
}

.yl-guardian-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-brand);
}

/* ===== Photo Upload ===== */
.yl-photo-upload-area {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.yl-photo-slot {
  width: 80px;
  height: 80px;
  border: 2px dashed var(--color-border);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.2s ease;
  background: #fff;
}

.yl-photo-slot:hover {
  border-color: var(--color-rose);
}

.yl-photo-slot.filled {
  border-style: solid;
  border-color: var(--color-rose);
}

.yl-photo-slot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.yl-photo-add {
  font-size: 24px;
  color: var(--color-text-light);
}

.yl-photo-uploading {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}

.yl-photo-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* ===== Cast Policy ===== */
.yl-cast-policy {
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(196, 149, 138, 0.2);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(250, 246, 243, 0.94));
  box-shadow: 0 10px 24px rgba(134, 102, 92, 0.06);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.yl-cast-policy-head {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.yl-cast-policy-badge {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(196, 149, 138, 0.12);
  color: var(--color-brand);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.yl-cast-policy-lead {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.6;
  color: var(--color-text);
  max-width: 40ch;
  word-break: keep-all;
  overflow-wrap: break-word;
}

.yl-cast-policy-highlights {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(136px, 1fr));
  gap: 12px;
  align-items: stretch;
}

.yl-policy-highlight {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 84px;
  padding: 14px 12px;
  border-radius: 12px;
  background: rgba(253, 248, 245, 0.92);
  border: 1px solid rgba(196, 149, 138, 0.14);
  text-align: left;
}

.yl-policy-highlight strong {
  display: block;
  font-size: 14px;
  color: var(--color-text);
  margin-bottom: 6px;
}

.yl-policy-highlight span {
  display: block;
  font-size: 11px;
  line-height: 1.45;
  color: var(--color-text-light);
  word-break: keep-all;
}

.yl-cast-policy-foot {
  padding-top: 12px;
  border-top: 1px solid rgba(196, 149, 138, 0.14);
  font-size: 12px;
  line-height: 1.6;
  color: var(--color-brand);
  max-width: 42ch;
  word-break: keep-all;
  overflow-wrap: break-word;
}

/* ===== Privacy ===== */
.yl-privacy-text {
  display: grid;
  gap: 10px;
  background: rgba(0, 0, 0, 0.02);
  padding: 16px;
  border-radius: 12px;
}

.yl-privacy-row {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  font-size: 12px;
  line-height: 1.6;
}

.yl-privacy-row strong {
  color: var(--color-text);
  font-weight: 600;
  white-space: nowrap;
}

.yl-privacy-row span {
  color: var(--color-text-light);
  word-break: keep-all;
  overflow-wrap: break-word;
}

/* ===== Checkbox ===== */
.yl-checkbox-group {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.yl-checkbox-group input[type="checkbox"] {
  margin-top: 2px;
  accent-color: var(--color-rose);
  flex-shrink: 0;
}

.yl-checkbox-group label {
  font-size: 12px;
  color: var(--color-text);
  line-height: 1.5;
  word-break: keep-all;
}

/* ===== Submit Button ===== */
.yl-cast-submit {
  width: 100%;
  min-height: 48px;
  padding: 0 20px;
  border: none;
  border-radius: var(--radius-pill);
  background: linear-gradient(135deg, #c4958a, #d4a574);
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  letter-spacing: 0.03em;
  font-family: inherit;
}

.yl-cast-submit:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.yl-cast-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.yl-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}

/* ===== Cast Success ===== */
.yl-cast-success {
  text-align: center;
  padding: 60px 20px;
}

.yl-success-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.yl-cast-success h3 {
  font-family: var(--font-brand);
  font-size: 20px;
  color: var(--color-text);
  margin-bottom: 8px;
}

.yl-cast-success p {
  font-size: 13px;
  color: var(--color-text-light);
  line-height: 1.6;
  margin-bottom: 16px;
  word-break: keep-all;
  overflow-wrap: break-word;
}

.yl-cast-success-note {
  margin: 0 auto 24px;
  max-width: 320px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(196, 149, 138, 0.08);
  color: var(--color-brand);
  font-size: 12px;
  line-height: 1.6;
  word-break: keep-all;
  overflow-wrap: break-word;
}

.yl-cast-success-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.yl-cast-success-actions button {
  padding: 10px 20px;
  border-radius: var(--radius-pill);
  font-size: 13px;
  cursor: pointer;
  transition: var(--transition);
  font-family: inherit;
}

.yl-btn-primary {
  background: linear-gradient(135deg, #c4958a, #d4a574);
  color: #fff;
  border: none;
}

.yl-btn-secondary {
  background: transparent;
  color: var(--color-brand);
  border: 1px solid var(--color-border);
}

.yl-btn-secondary:hover {
  border-color: var(--color-rose);
}

/* ===== Animations ===== */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== Desktop ===== */
@media (min-width: 600px) {
  .yl-card-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .intro-logo {
    width: 160px;
  }

  .yl-cast-block {
    padding: 24px;
  }

  .yl-cast-section {
    padding-inline: 20px;
  }
}

@media (max-width: 380px) {
  .yl-form-group-head {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 430px) {
  .yl-privacy-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
